import { isFunction } from '../../../utils/isFunction';

import { startApplePaySession } from './util/startApplePaySession';

declare global {
    interface Window {
        ApplePaySession?: ApplePaySession;
    }
}

/**
 * Messages that trust form sends to us
 */
export type ApplePayFrameMessageIn =
    | { type: '@apple-pay-trust/check-apple-pay'; payload: unknown }
    | { type: '@apple-pay-trust/start'; payload: unknown }
    | { type: '@apple-pay-trust/merchant-session'; payload: { session: unknown } }
    | { type: '@apple-pay-trust/finish'; payload: { success: boolean } }
    | { type: '@apple-pay-trust/destroy'; payload: unknown };

/**
 * Messages that we send to trust form
 */
export type ApplePayFrameMessageOut =
    | { type: '@apple-pay-trust/check-apple-pay-result'; payload: { ok: boolean } }
    | { type: '@apple-pay-trust/cancelled'; payload: unknown }
    | { type: '@apple-pay-trust/validate-merchant'; payload: { url: string } }
    | {
          type: '@apple-pay-trust/authorize-payment';
          payload: { token: ApplePayJS.ApplePayPaymentToken };
      };

export class ApplePayProvider {
    private canMakePayments = false;
    private session: ApplePaySession | null = null;

    private validateMerchantCallback: null | ((session: unknown) => void) = null;
    private authorizePaymentCallback: null | ((success: boolean) => void) = null;

    /**
     * Creates Apple Pay provider object, to supply trust form an access to Apple Pay API on embedding page domain
     *
     * @param {Window} target Trust binding form frame window (for postMessage)
     * @param {string} targetOrigin Target page origin (for postMessage)
     * @param {ApplePayJS.ApplePayPaymentRequest} request Apple Pay payment request
     */
    constructor(
        private target: Window,
        private targetOrigin: string,
        private request: ApplePayJS.ApplePayPaymentRequest,
    ) {
        if (window.ApplePaySession) {
            this.canMakePayments = ApplePaySession.canMakePayments();
        }
        this.listener = this.listener.bind(this);
    }

    /**
     * Start listening for requests from trust form
     */
    start() {
        window.addEventListener('message', this.listener);
    }

    /**
     * Stop listening for requests from trust form
     */
    stop() {
        window.removeEventListener('message', this.listener);

        if (this.session) {
            this.session.abort();
        }

        this.cleanupSession();
    }

    private cleanupSession() {
        this.validateMerchantCallback = null;
        this.authorizePaymentCallback = null;
        this.session = null;
    }

    private initiateSession() {
        if (!this.canMakePayments) {
            throw new Error('No Apple Pay support');
        }

        const onDone = () => {
            this.cleanupSession();
        };

        const onCancel = () => {
            this.target.postMessage(
                {
                    type: '@apple-pay-trust/cancelled',
                    payload: {},
                } satisfies ApplePayFrameMessageOut,
                this.targetOrigin,
            );
        };

        const onValidateMerchant = (url: string) => {
            if (isFunction(this.validateMerchantCallback)) {
                this.validateMerchantCallback(null);
                this.validateMerchantCallback = null;
            }

            this.target.postMessage(
                {
                    type: '@apple-pay-trust/validate-merchant',
                    payload: { url },
                } satisfies ApplePayFrameMessageOut,
                this.targetOrigin,
            );

            return new Promise<unknown>((resolve, reject) => {
                this.validateMerchantCallback = (session) => {
                    if (session) {
                        resolve(session);
                    } else {
                        reject();
                    }
                };
            });
        };

        const onAuthorizePayment = (token: ApplePayJS.ApplePayPaymentToken) => {
            if (isFunction(this.authorizePaymentCallback)) {
                this.authorizePaymentCallback(false);
                this.authorizePaymentCallback = null;
            }

            this.target.postMessage(
                {
                    type: '@apple-pay-trust/authorize-payment',
                    payload: { token },
                } satisfies ApplePayFrameMessageOut,
                this.targetOrigin,
            );

            return new Promise<void>((resolve, reject) => {
                this.authorizePaymentCallback = (success) => {
                    if (success) {
                        resolve();
                    } else {
                        reject();
                    }
                };
            });
        };

        this.session = startApplePaySession({
            request: this.request,
            onValidateMerchant,
            onAuthorizePayment,
            onCancel,
            onDone,
        });
    }

    private listener(evt: MessageEvent) {
        if (evt.origin && evt.origin !== this.targetOrigin) {
            return;
        }

        const msg = evt.data as ApplePayFrameMessageIn;

        if (!msg.type || !msg.type.startsWith('@apple-pay-trust')) {
            return;
        }

        switch (msg.type) {
            case '@apple-pay-trust/check-apple-pay':
                this.target.postMessage(
                    {
                        type: '@apple-pay-trust/check-apple-pay-result',
                        payload: { ok: this.canMakePayments },
                    } satisfies ApplePayFrameMessageOut,
                    this.targetOrigin,
                );

                break;

            case '@apple-pay-trust/start':
                this.initiateSession();
                break;

            case '@apple-pay-trust/merchant-session':
                {
                    const { session: merchantSession } = msg.payload;

                    if (isFunction(this.validateMerchantCallback)) {
                        this.validateMerchantCallback(merchantSession);
                        this.validateMerchantCallback = null;
                    }
                }
                break;

            case '@apple-pay-trust/finish':
                {
                    const { success } = msg.payload;

                    if (isFunction(this.authorizePaymentCallback)) {
                        this.authorizePaymentCallback(success);
                        this.authorizePaymentCallback = null;
                    }
                }
                break;

            case '@apple-pay-trust/destroy':
                if (this.session) {
                    this.session.abort();
                }
                this.cleanupSession();
                break;
        }
    }
}
